import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, PencilSimple, Trash, SignOut, Article, Envelope, X,
  UploadSimple, TextB, TextHOne, ListBullets, Users,
} from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail, resolveImg } from "../lib/api";
import Logo from "../components/Logo";

const emptyArticle = {
  title: "",
  excerpt: "",
  content: "",
  category: "Insight",
  cover_image: "/images/insights.png",
  read_time: "5 min read",
  published: true,
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [editing, setEditing] = useState(null); // article object or null
  const [form, setForm] = useState(emptyArticle);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const contentRef = useRef(null);
  const fileRef = useRef(null);

  useEffect(() => {
    if (user === false) navigate("/admin/login");
  }, [user, navigate]);

  const load = useCallback(() => {
    api.get("/articles?include_unpublished=true").then((r) => setArticles(r.data)).catch(() => {});
    api.get("/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/subscribers").then((r) => setSubscribers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (user && user.role) load();
  }, [user, load]);

  const uploadCover = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, cover_image: data.url }));
      toast.success("Cover image uploaded.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  // Rich-text helpers: transform the current selection in the content textarea
  const applyFormat = (kind) => {
    const el = contentRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const value = form.content;
    const sel = value.slice(start, end);
    let insert = sel;
    if (kind === "bold") insert = `**${sel || "bold text"}**`;
    if (kind === "heading") insert = `${start === 0 || value[start - 1] === "\n" ? "" : "\n\n"}## ${sel || "Heading"}\n\n`;
    if (kind === "list") insert = `${start === 0 || value[start - 1] === "\n" ? "" : "\n"}- ${sel || "List item"}\n`;
    const next = value.slice(0, start) + insert + value.slice(end);
    setForm((f) => ({ ...f, content: next }));
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = start + insert.length;
    });
  };

  const openNew = () => {
    setEditing("new");
    setForm(emptyArticle);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({ ...a });
  };
  const closeEditor = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    if (!form.title || !form.excerpt || !form.content) {
      toast.error("Title, excerpt and content are required.");
      return;
    }
    setSaving(true);
    try {
      if (editing === "new") {
        await api.post("/articles", form);
        toast.success("Article published.");
      } else {
        await api.put(`/articles/${editing.id}`, form);
        toast.success("Article updated.");
      }
      closeEditor();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.title}"?`)) return;
    try {
      await api.delete(`/articles/${a.id}`);
      toast.success("Article deleted.");
      load();
    } catch (err) {
      toast.error("Delete failed.");
    }
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  if (!user || !user.role) {
    return (
      <div className="grid min-h-screen place-items-center bg-cloud">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-200 border-t-navy" />
      </div>
    );
  }

  const inputCls =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-[15px] outline-none transition-colors duration-300 focus:border-navy";

  return (
    <div className="min-h-screen bg-cloud" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 glass-nav">
        <div className="mx-auto flex max-w-[1200px] items-center justify-between px-6 py-4">
          <Logo tagline={false} />
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-slate-500 sm:block">{user.email}</span>
            <button
              onClick={doLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-white"
            >
              <SignOut size={16} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-[1200px] px-6 py-10">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setTab("articles")}
            data-testid="tab-articles"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === "articles" ? "bg-navy text-white" : "bg-white text-slate-600"
            }`}
          >
            <Article size={16} /> Articles ({articles.length})
          </button>
          <button
            onClick={() => setTab("contacts")}
            data-testid="tab-contacts"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === "contacts" ? "bg-navy text-white" : "bg-white text-slate-600"
            }`}
          >
            <Envelope size={16} /> Inquiries ({contacts.length})
          </button>
          <button
            onClick={() => setTab("subscribers")}
            data-testid="tab-subscribers"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${
              tab === "subscribers" ? "bg-navy text-white" : "bg-white text-slate-600"
            }`}
          >
            <Users size={16} /> Subscribers ({subscribers.length})
          </button>
        </div>

        {tab === "articles" && (
          <div className="mt-8">
            <div className="flex items-center justify-between">
              <h1 className="font-heading text-2xl font-semibold text-navy">Insights</h1>
              <button
                onClick={openNew}
                data-testid="new-article-button"
                className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-semibold text-navy transition-transform hover:-translate-y-0.5"
              >
                <Plus size={16} weight="bold" /> New Article
              </button>
            </div>
            <div className="mt-6 space-y-3">
              {articles.map((a) => (
                <div
                  key={a.id}
                  data-testid={`admin-article-${a.slug}`}
                  className="flex items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-white p-5"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-bold uppercase tracking-wider text-teal">
                        {a.category}
                      </span>
                      {!a.published && (
                        <span className="rounded bg-slate-100 px-2 py-0.5 text-xs text-slate-500">
                          Draft
                        </span>
                      )}
                    </div>
                    <h3 className="mt-1 truncate font-heading text-lg font-semibold text-navy">
                      {a.title}
                    </h3>
                    <p className="mt-0.5 truncate text-sm text-slate-500">{a.excerpt}</p>
                  </div>
                  <div className="flex shrink-0 gap-2">
                    <button
                      onClick={() => openEdit(a)}
                      data-testid={`edit-${a.slug}`}
                      className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-navy hover:bg-navy hover:text-white"
                    >
                      <PencilSimple size={17} />
                    </button>
                    <button
                      onClick={() => remove(a)}
                      data-testid={`delete-${a.slug}`}
                      className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-red-500 hover:bg-red-500 hover:text-white"
                    >
                      <Trash size={17} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "contacts" && (
          <div className="mt-8">
            <h1 className="font-heading text-2xl font-semibold text-navy">Founder Inquiries</h1>
            <div className="mt-6 space-y-3">
              {contacts.length === 0 && (
                <p className="rounded-2xl border border-slate-100 bg-white p-6 text-slate-500">
                  No inquiries yet.
                </p>
              )}
              {contacts.map((c) => (
                <div
                  key={c.id}
                  data-testid={`contact-row-${c.id}`}
                  className="rounded-2xl border border-slate-100 bg-white p-6"
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="font-heading text-lg font-semibold text-navy">
                      {c.name}{" "}
                      {c.company && <span className="text-slate-400">· {c.company}</span>}
                    </h3>
                    <span className="text-xs text-slate-400">
                      {new Date(c.created_at).toLocaleString()}
                    </span>
                  </div>
                  <div className="mt-1 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-500">
                    <a href={`mailto:${c.email}`} className="text-teal hover:underline">
                      {c.email}
                    </a>
                    {c.website && <span>{c.website}</span>}
                    {c.stage && <span className="font-medium text-navy">{c.stage}</span>}
                  </div>
                  <p className="mt-3 rounded-xl bg-cloud p-4 leading-relaxed text-slate-600">
                    {c.message}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "subscribers" && (
          <div className="mt-8">
            <h1 className="font-heading text-2xl font-semibold text-navy">Newsletter Subscribers</h1>
            <div className="mt-6 overflow-hidden rounded-2xl border border-slate-100 bg-white">
              {subscribers.length === 0 ? (
                <p className="p-6 text-slate-500">No subscribers yet.</p>
              ) : (
                subscribers.map((s, i) => (
                  <div
                    key={s.email}
                    data-testid={`subscriber-row-${i}`}
                    className="flex items-center justify-between gap-4 border-b border-slate-100 px-6 py-4 last:border-0"
                  >
                    <a href={`mailto:${s.email}`} className="font-medium text-navy hover:text-gold">
                      {s.email}
                    </a>
                    <span className="text-xs text-slate-400">
                      {new Date(s.created_at).toLocaleDateString()}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-navy/40 p-4 backdrop-blur-sm" data-testid="article-editor">
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-[24px] bg-white p-8">
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-2xl font-semibold text-navy">
                {editing === "new" ? "New Article" : "Edit Article"}
              </h2>
              <button
                onClick={closeEditor}
                data-testid="close-editor"
                className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-500 hover:text-navy"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={save} className="mt-6 space-y-4">
              <input
                data-testid="article-title"
                className={inputCls}
                placeholder="Title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
              <div className="grid gap-4 sm:grid-cols-2">
                <input
                  data-testid="article-category"
                  className={inputCls}
                  placeholder="Category"
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                />
                <input
                  data-testid="article-readtime"
                  className={inputCls}
                  placeholder="Read time (e.g. 5 min read)"
                  value={form.read_time}
                  onChange={(e) => setForm({ ...form, read_time: e.target.value })}
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-navy">Cover Image</label>
                <div className="flex items-center gap-4">
                  <div className="h-20 w-28 shrink-0 overflow-hidden rounded-xl border border-slate-200 bg-cloud">
                    {form.cover_image && (
                      <img
                        src={resolveImg(form.cover_image)}
                        alt="cover preview"
                        className="h-full w-full object-cover"
                      />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      data-testid="article-cover-file"
                      className="hidden"
                      onChange={uploadCover}
                    />
                    <button
                      type="button"
                      data-testid="article-cover-upload"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="inline-flex items-center gap-2 rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-navy transition-colors hover:bg-navy hover:text-white disabled:opacity-60"
                    >
                      <UploadSimple size={16} /> {uploading ? "Uploading..." : "Upload Image"}
                    </button>
                    <input
                      data-testid="article-cover"
                      className={`${inputCls} mt-3`}
                      placeholder="…or paste an image URL"
                      value={form.cover_image}
                      onChange={(e) => setForm({ ...form, cover_image: e.target.value })}
                    />
                  </div>
                </div>
              </div>
              <textarea
                data-testid="article-excerpt"
                rows={2}
                className={`${inputCls} resize-none`}
                placeholder="Short excerpt"
                value={form.excerpt}
                onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
              />
              <div>
                <div className="mb-2 flex items-center gap-2">
                  <span className="text-sm font-medium text-navy">Content</span>
                  <div className="ml-auto flex items-center gap-1.5">
                    <button
                      type="button"
                      data-testid="format-bold"
                      onClick={() => applyFormat("bold")}
                      title="Bold"
                      className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-navy hover:bg-navy hover:text-white"
                    >
                      <TextB size={16} weight="bold" />
                    </button>
                    <button
                      type="button"
                      data-testid="format-heading"
                      onClick={() => applyFormat("heading")}
                      title="Heading"
                      className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-navy hover:bg-navy hover:text-white"
                    >
                      <TextHOne size={16} />
                    </button>
                    <button
                      type="button"
                      data-testid="format-list"
                      onClick={() => applyFormat("list")}
                      title="List"
                      className="grid h-8 w-8 place-items-center rounded-lg border border-slate-200 text-navy hover:bg-navy hover:text-white"
                    >
                      <ListBullets size={16} />
                    </button>
                  </div>
                </div>
                <textarea
                  ref={contentRef}
                  data-testid="article-content"
                  rows={11}
                  className={`${inputCls} resize-none font-mono text-[13px] leading-relaxed`}
                  placeholder="Write your article. Use the toolbar for **bold**, ## headings and - lists. Leave a blank line between paragraphs."
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                />
                <p className="mt-1.5 text-xs text-slate-400">
                  Formatting: <b>**bold**</b>, <b>## Heading</b>, <b>- list item</b>. Blank line = new paragraph.
                </p>
              </div>
              <label className="flex items-center gap-3 text-sm text-navy">
                <input
                  data-testid="article-published"
                  type="checkbox"
                  checked={form.published}
                  onChange={(e) => setForm({ ...form, published: e.target.checked })}
                  className="h-4 w-4 accent-navy"
                />
                Published (visible on site)
              </label>
              <button
                type="submit"
                data-testid="save-article"
                disabled={saving}
                className="w-full rounded-full bg-navy px-6 py-3.5 font-semibold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60"
              >
                {saving ? "Saving..." : editing === "new" ? "Publish Article" : "Save Changes"}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
